import * as React from 'react';
import {useRef} from 'react';
import {FormattedMessage} from 'react-intl';

import {LOCALE, SITE} from 'features/ui/enums';
import {useModal} from 'features/modal/hooks/useModal';
import {LoginForm} from 'components/forms/LoginForm';
import {StageStatus} from 'features/search/filters/enums';
import {getFormattedMessage} from 'features/services/utils/language-helper';
import {getCurrentLocale, getCurrentSite} from 'features/services/utils/env-helper';

import './style.scss';

import {Icon} from '@atoms/Media/Icon';
import {Button, ButtonColor, ButtonSize, ButtonState, ButtonType} from 'components/atoms/LinksAndButtons/Button';
import {VipPeriodWindow} from '@organisms/VipPeriodWindow';
import {useNavigate} from 'react-router';
import {ResidenceObjectViewModel} from "features/template/viewModels/ResidenceObject";

import {useAuth} from "features/authentication/hooks/useAuth";

interface Props {
    onClick: () => void;
    gtmFunc?: () => void;
    object: ResidenceObjectViewModel;
    setPurchaseComplete: (complete: boolean) => void;
}

export const PurchaseButton: React.FunctionComponent<Props> = ({
    onClick: _onClick, gtmFunc,
    setPurchaseComplete: _setPurchaseComplete,
    object
}) => {
    const { open } = useModal();
    const {user, isLoggedIn} = useAuth();
    const navigate = useNavigate();
    const purchaseBtnRef = useRef<HTMLButtonElement>(null);

    const purchaseClick = () => {
        if (!isLoggedIn) {
            const text = object.stageStatus !== StageStatus.VIP && getCurrentLocale() === LOCALE.NO
                ? getFormattedMessage('jm.components.StagePage.purchaseModalTextTillbud')
                : getFormattedMessage('jm.components.StagePage.purchaseModalText');


            open(
                <LoginForm
                    title={getFormattedMessage('jm.components.StagePage.purchaseModalHeadline')}
                    text={text}
                    hideTypeToggle
                />,
                {fullscreen: false, ariaLabel: getFormattedMessage('jm.components.LoginForm.ariaLabel')}
            );

            return;
        }

        if(!user) return;

        void gtmFunc;

        if(getCurrentSite() === SITE.NO) {
            navigate({
                pathname: `/kjopetilbud/${object.objectId}`,
            });
            return;
        }

        open(
            <VipPeriodWindow
                residence={object}
                stageUrl={object.stageUrl}
                purchaseSignupTermsText={object.purchaseSignupTermsText}
                vipPurchaseSignupModalText={object.vipPurchaseSignupModalText}
            />,
            {fullscreen: false, ariaLabel: getFormattedMessage('jm.components.VipPeriodWindow.ariaLabel')}
        );
    };

    return (
        <Button
            onClick={purchaseClick}
            buttonState={user?.hasPurchase(object.objectId)
                ? ButtonState.INACTIVE
                : ButtonState.DEFAULT
            }
            ref={purchaseBtnRef} 
            buttonColor={ButtonColor.PURCHASE} 
            buttonSize={ButtonSize.DEFAULT} 
            buttonType={ButtonType.DEFAULT} 
        >
            {user?.hasPurchase(object.objectId) && <Icon name="check" size={16} />}
            <FormattedMessage
                id={user?.hasPurchase(object.objectId)
                    ? "jm.components.ObjectPage.buttonTextPurchaseDone"
                    : "jm.components.ObjectPage.buttonTextPurchase"}
            />
        </Button>
    )
};
